import { Typography, Box, TextField, IconButton, Select, FormControl, InputLabel, MenuItem } from "@mui/material"
import { EEslTemplateElementType } from "../../../models/enums/eslTemplateType"
import ImageElementPropreties from "./elementsPropreties/ImageElementPropreties";
import ShapeElementPropreties from "./elementsPropreties/ShapeElementPropreties";
import TextElementPropreties from "./elementsPropreties/TextElementPropreties";
import QrCodeElementPropreties from "./elementsPropreties/QrCodeElementPropreties";
import IEslTemplateItem from "../../../models/esl/eslTemplateElement";
import DeleteIcon from '@mui/icons-material/Delete';
import EditOnly from '../../permissions/EditOnly';
import { useTranslation } from "react-i18next";
import { ELanguage } from "../../../models/enums/language";
import DatetimeElementPropreties from "./elementsPropreties/DatetimeElementPropreties";

interface IElementsGridProps {
  selectedElement: IEslTemplateItem;
  onUpdateElement: (element: IEslTemplateItem) => void;
  onDeleteElement: (id: number) => void;
  templateWidth: number;
  templateHeight: number;
}

const ElementPropreties = ({ selectedElement, onUpdateElement, onDeleteElement, templateWidth, templateHeight }: IElementsGridProps) => {
  const { t } = useTranslation(['eslTemplate', 'common']);

  const elementsPropretiesMap = new Map<EEslTemplateElementType, JSX.Element>([
    [EEslTemplateElementType.Text, <TextElementPropreties selectedElement={selectedElement} onUpdateElement={onUpdateElement} />],
    [EEslTemplateElementType.MultilineText, <TextElementPropreties selectedElement={selectedElement} onUpdateElement={onUpdateElement} />],
    [EEslTemplateElementType.Image, <ImageElementPropreties selectedElement={selectedElement} onUpdateElement={onUpdateElement} />],
    [EEslTemplateElementType.Rectangle, <ShapeElementPropreties selectedElement={selectedElement} onUpdateElement={onUpdateElement} />],
    [EEslTemplateElementType.Circle, <ShapeElementPropreties selectedElement={selectedElement} onUpdateElement={onUpdateElement} />],
    [EEslTemplateElementType.Line, <ShapeElementPropreties selectedElement={selectedElement} onUpdateElement={onUpdateElement} />],
    [EEslTemplateElementType.LastCleaningDatetime, <DatetimeElementPropreties selectedElement={selectedElement} onUpdateElement={onUpdateElement} />],
    [EEslTemplateElementType.ServiceRequestQRCode, (
      <QrCodeElementPropreties
        selectedElement={selectedElement}
        onUpdateElement={onUpdateElement}
        templateWidth={templateWidth}
        templateHeight={templateHeight}
      />
    )],
  ]);

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h6">{t(`eslTemplate:templateEditor.elementsPicker.${EEslTemplateElementType[selectedElement.type]}`)}</Typography>
        <EditOnly>
          <IconButton color='error' onClick={() => onDeleteElement(selectedElement.id)}>
            <DeleteIcon />
          </IconButton>
        </EditOnly>
      </Box>

      <Box sx={{ mb: 2 }}>
        {selectedElement.type !== EEslTemplateElementType.Text
          && (
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                label={t('eslTemplate:templateEditor.elementPropreties.width')}
                name="width"
                type="number"
                value={selectedElement.dimensions.width}
                onChange={(e) => onUpdateElement({ ...selectedElement, dimensions: { ...selectedElement.dimensions, width: Number(e.target.value) } })}
                fullWidth
              />

              {selectedElement.type !== EEslTemplateElementType.Line && (
                <TextField
                  label={t('eslTemplate:templateEditor.elementPropreties.height')}
                  name="height"
                  type="number"
                  value={selectedElement.dimensions.height}
                  onChange={(e) => onUpdateElement({ ...selectedElement, dimensions: { ...selectedElement.dimensions, height: Number(e.target.value) } })}
                  fullWidth
                />
              )}
            </Box>
          )}

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <TextField
            label={t('eslTemplate:templateEditor.elementPropreties.top')}
            name="top"
            type="number"
            value={selectedElement.position.top}
            onChange={(e) => onUpdateElement({ ...selectedElement, position: { ...selectedElement.position, top: Number(e.target.value) } })}
            fullWidth
          />
          <TextField
            label={t('eslTemplate:templateEditor.elementPropreties.left')}
            name="left"
            type="number"
            value={selectedElement.position.left}
            onChange={(e) => onUpdateElement({ ...selectedElement, position: { ...selectedElement.position, left: Number(e.target.value) } })}
            fullWidth
          />
        </Box>
      </Box>

      {elementsPropretiesMap.get(selectedElement.type)}
    </Box>
  )
}

export default ElementPropreties